import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth } from '../lib/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { coreApiFetch } from '../lib/apiClient';

const UserContext = createContext(null);

export const UserProvider = ({ children }) => {
  const [authReady, setAuthReady] = useState(false);

  const [userState, setUserState] = useState({
    loading: true,
    plan: 'free',
    status: 'active',
    usage: null,
    limits: null,
  });

  const fetchUser = async (attempt = 1) => {
    try {
      const data = await coreApiFetch('/api/user/me', { method: 'GET' });

      setUserState({
        loading: false,
        plan: (data.plan || 'free').toLowerCase(),
        status: data.status || 'active',
        usage: data.usage || null,
        limits: data.limits || null,
      });
    } catch (err) {
      if (attempt < 3) {
        setTimeout(() => fetchUser(attempt + 1), 1500);
        return;
      }

      setUserState({
        loading: false,
        plan: 'free',
        status: 'active',
        usage: null,
        limits: null,
      });
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setAuthReady(true);

      if (!user) {
        setUserState({
          loading: false,
          plan: 'free',
          status: 'active',
          usage: null,
          limits: null,
        });
        return;
      }

      setUserState((prev) => ({ ...prev, loading: true }));
      fetchUser();
    });

    // 🔁 Subscription / usage refresh
    const onSubscriptionUpdate = () => fetchUser();
    window.addEventListener('nt:subscription-updated', onSubscriptionUpdate);

    return () => {
      unsubscribe();
      window.removeEventListener(
        'nt:subscription-updated',
        onSubscriptionUpdate
      );
    };
  }, []);

  const refreshUser = async () => {
    await fetchUser();
  };

  return (
    <UserContext.Provider
      value={{
        ...userState,
        authReady,
        refreshUser,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
